import React, { useState, useEffect } from 'react';
import { leavesAPI, employeesAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  Calendar,
  BarChart3,
  Search,
  X,
  Briefcase,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getNormalizedBalance = (balance = {}) => {
  const totalDays = toNumber(balance.totalDays ?? balance.total);
  const usedDays = toNumber(balance.usedDays ?? balance.used);
  const pendingDays = toNumber(balance.pendingDays ?? balance.pending);
  const availableDays = Math.max(0, totalDays - usedDays - pendingDays);
  const usedPercentage =
    totalDays > 0 ? Math.min(100, Math.max(0, (usedDays / totalDays) * 100)) : 0;

  return {
    totalDays,
    usedDays,
    pendingDays,
    availableDays,
    usedPercentage,
  };
};

const LeaveManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('types');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [myBalances, setMyBalances] = useState([]);
  const [onLeaveToday, setOnLeaveToday] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setUserRole(user?.role || 'employee');
    loadLeaveTypes();
    loadEmployees();
    loadMyBalances();
    loadLeaveStats();
  }, []);

  const loadLeaveTypes = async () => {
    try {
      const response = await leavesAPI.getLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      // Silently fail
    }
  };

  const loadEmployees = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user?.role && ['admin', 'supervisor', 'manager', 'hr'].includes(user.role)) {
        const response = await employeesAPI.getAll();
        setEmployees(response.data);
      }
    } catch (error) {
      // Silently fail for non-authorized users
    }
  };

  const loadMyBalances = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await leavesAPI.getMyLeaveBalances(currentYear);
      setMyBalances(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMyBalances([]);
    }
  };

  const loadLeaveStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const endDate = nextMonth.toISOString().split('T')[0];

      const response = await leavesAPI.getLeaveCalendar(today, endDate);
      const leaves = response.data || [];

      const todayLeaves = leaves.filter(leave => {
        const start = new Date(leave.startDate);
        const end = new Date(leave.endDate);
        const now = new Date(today);
        return start <= now && end >= now;
      });

      const upcoming = leaves.filter(leave => {
        const start = new Date(leave.startDate);
        const now = new Date(today);
        return start > now;
      }).slice(0, 5);

      setOnLeaveToday(todayLeaves);
      setUpcomingLeaves(upcoming);
    } catch (error) {
      setOnLeaveToday([]);
      setUpcomingLeaves([]);
    }
  };

  const handleCreateLeaveType = () => {
    if (!['admin', 'supervisor', 'manager', 'hr'].includes(userRole)) {
      toast.error('Access denied.');
      return;
    }
    setModalType('leaveType');
    setSelectedItem(null);
    setFormData({
      name: '',
      description: '',
      defaultDays: 0,
      requiresApproval: true,
      requiresDocument: false,
      color: '#3B82F6'
    });
    setShowModal(true);
  };

  const handleEditLeaveType = (leaveType) => {
    if (!['admin', 'supervisor', 'manager', 'hr'].includes(userRole)) {
      toast.error('Access denied.');
      return;
    }
    setModalType('leaveType');
    setSelectedItem(leaveType);
    setFormData(leaveType);
    setShowModal(true);
  };

  const handleSaveLeaveType = async () => {
    try {
      if (selectedItem) {
        await leavesAPI.updateLeaveType(selectedItem._id, formData);
        toast.success('Leave type updated');
      } else {
        await leavesAPI.createLeaveType(formData);
        toast.success('Leave type created');
      }
      setShowModal(false);
      loadLeaveTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save leave type');
    }
  };

  const handleDeleteLeaveType = async (id) => {
    if (!['admin', 'supervisor', 'manager', 'hr'].includes(userRole)) {
      toast.error('Access denied.');
      return;
    }
    if (window.confirm('Delete this leave type?')) {
      try {
        await leavesAPI.deleteLeaveType(id);
        toast.success('Leave type deleted');
        loadLeaveTypes();
      } catch (error) {
        toast.error('Failed to delete leave type');
      }
    }
  };

  const handleAssignLeaves = () => {
    setModalType('assignLeaves');
    setFormData({
      employeeIds: [],
      leaveTypeId: '',
      totalDays: 0,
      year: new Date().getFullYear()
    });
    setShowModal(true);
  };

  const handleSaveLeaveAssignment = async () => {
    try {
      if (!formData.employeeIds.length || !formData.leaveTypeId) {
        toast.error('Please select employees and leave type');
        return;
      }
      await leavesAPI.bulkAssignLeaveBalances({
        employeeIds: formData.employeeIds,
        leaveTypeId: formData.leaveTypeId,
        totalDays: formData.totalDays,
        year: formData.year
      });
      toast.success('Leave balances assigned');
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to assign balances');
    }
  };

  const tabs = [
    { id: 'types', name: 'Leave Types', icon: Calendar },
    { id: 'balances', name: 'My Balances', icon: Users },
    { id: 'stats', name: 'Overview', icon: BarChart3 }
  ];

  return (
    <div className="">
      <div className=" space-y-6">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-gray-500" />
              Leave Management
            </h1>
            <p className="text-slate-500 mt-1">Manage policies, balances, and analytics</p>
          </div>
          <button
            onClick={() => navigate('/leave-application')}
            className="inline-flex items-center px-5 py-2.5 bg-gray-900 text-white font-semibold rounded-md shadow-none transition-all  "
          >
            <Plus className="w-5 h-5 mr-2" />
            Apply Leave
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white p-1.5 rounded-lg shadow-sm border border-slate-200 inline-flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id
                  ? 'bg-gray-100 text-gray-900 shadow-sm ring-1 ring-gray-200'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
            >
              <tab.icon size={16} />
              {tab.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Leave Types Tab */}
          {activeTab === 'types' && (
            <motion.div
              key="types"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="flex justify-end mb-4">
                {['admin', 'supervisor', 'manager', 'hr'].includes(userRole) && (
                  <button
                    onClick={handleCreateLeaveType}
                    className="inline-flex items-center px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Leave Type
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {leaveTypes.map((leaveType) => (
                  <div key={leaveType._id} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-all group">
                    <div className="h-1.5 w-full" style={{ backgroundColor: leaveType.color }}></div>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-slate-50 text-sm font-semibold shadow-inner" style={{ color: leaveType.color }}>
                            {leaveType.name.charAt(0)}
                          </div>
                          <h3 className="text-lg font-medium text-gray-900">{leaveType.name}</h3>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {['admin', 'supervisor', 'manager', 'hr'].includes(userRole) && (
                            <>
                              <button onClick={() => handleEditLeaveType(leaveType)} className="p-2 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                                <Edit2 size={16} />
                              </button>
                              <button onClick={() => handleDeleteLeaveType(leaveType._id)} className="p-2 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-slate-500 mb-6 line-clamp-2 min-h-[40px]">{leaveType.description}</p>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="px-3 py-2 bg-slate-50 rounded-md border border-slate-100">
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Default Days</span>
                          <span className="font-medium text-gray-900 text-lg">{leaveType.defaultDays}</span>
                        </div>
                        <div className={`px-3 py-2 rounded-md border ${leaveType.requiresApproval ? 'bg-gray-100 border-gray-200' : 'bg-gray-100 border-gray-200'}`}>
                          <span className={`text-xs font-medium block mb-1 ${leaveType.requiresApproval ? 'text-gray-700' : 'text-gray-700'}`}>Approval</span>
                          <div className={`font-bold flex items-center gap-1 ${leaveType.requiresApproval ? 'text-gray-700' : 'text-gray-900'}`}>
                            {leaveType.requiresApproval ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
                            {leaveType.requiresApproval ? 'Required' : 'Auto'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Balances Tab */}
          {activeTab === 'balances' && (
            <motion.div
              key="balances"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {userRole === 'employee' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {leaveTypes.map((leaveType) => {
                    const balance = myBalances.find(b => {
                      const balanceLeaveTypeId = b.leaveTypeId?._id || b.leaveTypeId;
                      return balanceLeaveTypeId === leaveType._id;
                    }) || { totalDays: 0, usedDays: 0, pendingDays: 0 };
                    const normalized = getNormalizedBalance(balance);

                    const available = normalized.availableDays;
                    const percentage = normalized.usedPercentage;

                    return (
                      <div key={leaveType._id} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative">
                        <div className="absolute right-0 top-0 w-24 h-24 bg-gradient-to-br from-transparent to-slate-50 rounded-bl-full -z-0" style={{ opacity: 0.5 }}></div>
                        <div className="p-6 relative z-10">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-slate-50 font-bold text-lg" style={{ color: leaveType.color }}>
                              {leaveType.name.charAt(0)}
                            </div>
                            <div>
                              <h3 className="font-medium text-gray-900">{leaveType.name}</h3>
                              <span className="text-xs text-slate-500 font-medium">Annual Quota: {normalized.totalDays} days</span>
                            </div>
                          </div>

                          <div className="flex items-end justify-between mb-2">
                            <div>
                              <span className="text-3xl font-medium text-gray-900">{available}</span>
                              <span className="text-sm text-slate-500 ml-1">left</span>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-slate-700">{normalized.usedDays}</span>
                              <span className="text-xs text-slate-400 ml-1">used</span>
                            </div>
                          </div>

                          <div className="w-full bg-slate-100 rounded-full h-2 mb-4 overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${percentage}%`, backgroundColor: leaveType.color }}></div>
                          </div>

                          {normalized.pendingDays > 0 && (
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-gray-100 px-3 py-2 rounded-lg border border-gray-200">
                              <AlertCircle size={14} />
                              {normalized.pendingDays} days pending approval
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-6">
                  {['admin', 'supervisor', 'manager', 'hr'].includes(userRole) && (
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                            <Users className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 text-lg">Employee Balances</h3>
                            <p className="text-slate-500 text-sm">Assign quotas or view usage.</p>
                          </div>
                        </div>
                        <div className="flex gap-3 w-full md:w-auto">
                          <div className="relative flex-1 md:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              placeholder="Search employee..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:bg-white transition-all text-sm"
                            />
                          </div>
                          <button
                            onClick={handleAssignLeaves}
                            className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md transition-colors shadow-none flex items-center gap-2"
                          >
                            <Plus size={18} /> Assign
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Additional content for admin view of all balances could go here */}
                </div>
              )}
            </motion.div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {[
                { title: `On Leave Today (${onLeaveToday.length})`, icon: Users, data: onLeaveToday, emptyMsg: 'No one on leave today', color: 'blue' },
                { title: 'Upcoming Leaves', icon: Calendar, data: upcomingLeaves, emptyMsg: 'No upcoming leaves', color: 'indigo' }
              ].map((section, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm border border-slate-200 flex flex-col h-full">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${section.color}-50 text-${section.color}-600`}>
                      <section.icon size={20} />
                    </div>
                    <h3 className="font-medium text-gray-900">{section.title}</h3>
                  </div>
                  <div className="p-4 space-y-2 flex-1">
                    {section.data.length > 0 ? (
                      section.data.map(leave => (
                        <div key={leave._id} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-md transition-colors border border-transparent hover:border-slate-100">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm" style={{ backgroundColor: leave.leaveTypeId?.color }}>
                              {leave.employeeId?.name?.[0]}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 text-sm">{leave.employeeId?.name}</div>
                              <div className="text-[10px] font-semibold text-slate-500 uppercase">{leave.leaveTypeId?.name}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-bold text-slate-700">{leave.totalDays}d</div>
                            <div className="text-[10px] text-slate-400">{new Date(leave.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="h-40 flex flex-col items-center justify-center text-slate-400">
                        <section.icon size={32} className="mb-2 opacity-50" />
                        <p className="text-sm">{section.emptyMsg}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
                className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                  <h3 className="text-lg font-medium text-gray-900">
                    {modalType === 'leaveType'
                      ? (selectedItem ? 'Edit Leave Type' : 'Create Leave Type')
                      : 'Assign Leave Balances'
                    }
                  </h3>
                  <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                    <X size={20} className="text-slate-400" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  {modalType === 'leaveType' && (
                    <>
                      <FormInput label="Name" value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Sick Leave" />

                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Description</label>
                        <textarea
                          value={formData.description || ''}
                          onChange={e => setFormData({ ...formData, description: e.target.value })}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm resize-none"
                          rows="2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormInput label="Default Days" type="number" value={formData.defaultDays || 0} onChange={e => setFormData({ ...formData, defaultDays: parseInt(e.target.value) })} />
                        <div>
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Color</label>
                          <div className="flex gap-2">
                            <input type="color" value={formData.color || '#3B82F6'} onChange={e => setFormData({ ...formData, color: e.target.value })} className="h-10 w-12 rounded-lg cursor-pointer border-0 p-0" />
                            <div className="flex-1 h-10 rounded-lg border border-slate-200 flex items-center px-3 text-xs font-mono text-slate-500 bg-slate-50">
                              {formData.color}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-50/80 p-4 rounded-md space-y-3 border border-slate-100">
                        <Toggle label="Requires Manager Approval" checked={formData.requiresApproval || false} onChange={e => setFormData({ ...formData, requiresApproval: e.target.checked })} />
                        <Toggle label="Requires Documents" checked={formData.requiresDocument || false} onChange={e => setFormData({ ...formData, requiresDocument: e.target.checked })} />
                      </div>
                    </>
                  )}

                  {modalType === 'assignLeaves' && (
                    <>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Employees</label>
                        <div className="border border-slate-200 rounded-md h-48 overflow-y-auto p-2 scrollbar-thin">
                          {employees.map(emp => (
                            <label key={emp._id} className="flex items-center p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                className="rounded text-gray-700 focus:ring-gray-200 mr-3 border-slate-300"
                                checked={formData.employeeIds?.includes(emp._id)}
                                onChange={(e) => {
                                  const ids = formData.employeeIds || [];
                                  if (e.target.checked) setFormData({ ...formData, employeeIds: [...ids, emp._id] });
                                  else setFormData({ ...formData, employeeIds: ids.filter(id => id !== emp._id) });
                                }}
                              />
                              <span className="text-sm font-medium text-slate-700">{emp.name}</span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-slate-400 mt-1.5 text-right">{formData.employeeIds?.length || 0} selected</p>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">Leave Type</label>
                        <select
                          value={formData.leaveTypeId || ''}
                          onChange={(e) => setFormData({ ...formData, leaveTypeId: e.target.value })}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm bg-white"
                        >
                          <option value="">Select Type</option>
                          {leaveTypes.map(lt => (
                            <option key={lt._id} value={lt._id}>{lt.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormInput label="Year" type="number" value={formData.year || new Date().getFullYear()} onChange={e => setFormData({ ...formData, year: parseInt(e.target.value) })} />
                        <FormInput label="Days to Add" type="number" value={formData.totalDays || 0} onChange={e => setFormData({ ...formData, totalDays: parseInt(e.target.value) })} />
                      </div>
                    </>
                  )}
                </div>

                <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-white border border-slate-200 rounded-md text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={modalType === 'leaveType' ? handleSaveLeaveType : handleSaveLeaveAssignment}
                    className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-md shadow-none transition-colors"
                  >
                    {modalType === 'leaveType' ? 'Save' : 'Assign'}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Helper Components
const FormInput = ({ label, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm"
      placeholder={placeholder}
    />
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center cursor-pointer justify-between group">
    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors">{label}</span>
    <div className="relative">
      <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
      <div className={`w-10 h-6 bg-slate-200 rounded-full shadow-inner transition-colors ${checked ? 'bg-gray-100' : ''}`}></div>
      <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${checked ? 'translate-x-4' : ''}`}></div>
    </div>
  </label>
);

export default LeaveManagement;
